<template>
  <div class="pack">
    <toolbar transparent fixed :logo="false">
      <template v-slot:right>
        <toolbar-share-button />
      </template>
    </toolbar>

    <template v-if="isPresent(banner) || isPresent(video)">
      <hero :image="banner" :video="video" safe-padding />
    </template>
    <template v-else>
      <div class="v-toolbar__spacer"></div>
    </template>

    <text-block-header
      :no-collapse="hasNoItems"
      :heading="title"
      :content="description"
    />

    <div v-if="showEventsBlock" class="pack__events bg-white py-3 mb-3">
      <div class="collection__headings px-2">
        <h3 class="collection__heading">
          Events
          <v-link to="/discover/events">Browse all events</v-link>
        </h3>
        <p class="pack__passes-subheading">
          <v-icon icon="local_activity" />
          <template
            v-if="
              hasActiveSubscription &&
                (remainingCreditsNumber || remainingCreditsNumber === 0)
            "
          >
            <span v-if="remainingCreditsNumber === 0">
              No active passes left
            </span>
            <span v-else>
              <b>{{ remainingCreditsNumber }}</b>
              passes remaining for these events
            </span>
          </template>
          <span v-else>
            Includes
            <b>{{ credits_number }}</b>
            {{ creditsText }} to these events
          </span>
        </p>
      </div>
      <collection :show-price="false" direction="horizontal" :items="events" />
    </div>

    <collection
      v-if="isPresent(nonEventItems)"
      :show-price="false"
      class="bg-white"
      heading="Pack Content"
      direction="vertical"
      :unlock="hasActiveSubscription"
      :items="nonEventItems"
    />

    <template
      v-if="isPresent(product) && this.initializedPath === this.$route.path"
    >
      <cta-row v-if="ctaInfo" white-bg row>
        <cta-row-price
          :sub-text="ctaInfo.subText"
          :priceText="priceText"
          :price-interval="priceInterval"
        />
        <v-button class="mr-0" :is-loading="isLoading" @click="handleCtaClick">
          {{ ctaInfo.buttonText }}
        </v-button>
      </cta-row>

      <checkout-modal :product="product" @success="handlePackPurchase" />
    </template>

    <alert-dialog
      :actions="[
        {
          title: 'View Receipt',
          callback: this.goToAccount
        },
        {
          title: 'Go To Pack',
          callback: () => (this.showSuccessDialog = false)
        }
      ]"
      :show="showSuccessDialog"
      @close="() => (this.showSuccessDialog = false)"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
      <p class="font-semi-bold">You successfully purchased the pack!</p>
    </alert-dialog>

    <v-made-with-movement v-if="!isDesktop" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex"
import urljoin from "url-join"

import { isPresent, isEmpty } from "@/lib/utils"
import { toDate } from "@/lib/date-utils"
import Toolbar from "@/components/Toolbar"
import ToolbarShareButton from "@/components/toolbar/ShareButton"
import Hero from "@/components/Hero"
import TextBlockHeader from "@/components/TextBlockHeader"
import Collection from "@/components/Collection"

export default {
  components: {
    Toolbar,
    ToolbarShareButton,
    Hero,
    TextBlockHeader,
    Collection,
    CheckoutModal: () => import("@/components/checkout/CheckoutModal"),
    CtaRow: () => import("@/components/CtaRow"),
    CtaRowPrice: () => import("@/components/cta-row/Price"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    id: { type: [Number, String], required: true },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    banner: { type: Object, default: () => ({}) },
    video: { type: Object, default: () => ({}) },
    product: { type: Object, default: null },
    has_event_credits: { type: Boolean, required: true },
    credits_number: { type: Number, default: 0 },
    items: { type: Array, required: true }
  },

  data() {
    return {
      isLoading: false,
      showSuccessDialog: false,
      remainingCreditsNumber: null,
      initializedPath: this.$route.path
    }
  },

  mounted() {
    this.initializedPath = this.$route.path
  },

  activated() {
    this.loadCreditsInfo()
  },

  computed: {
    ...mapState("responsive", ["isDesktop"]),
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("packs", ["userPackSubscription"]),

    ctaInfo() {
      // TODO: add tests
      if (this.hasActiveSubscription) {
        if (this.isCanceled) {
          return {
            subText: this.canceledSubtext,
            buttonText: "Restart"
          }
        }
      } else {
        if (this.isRecurring) {
          return {
            priceInterval: `/ ${this.product.interval}`,
            subText: "Cancel Anytime",
            buttonText: "Subscribe"
          }
        } else {
          return {
            subText: "Purchase Today",
            buttonText: "Buy now"
          }
        }
      }

      return null
    },

    showEventsBlock() {
      return this.has_event_credits && this.events.length > 0
    },

    events() {
      return this.items
        .filter(i => i.type === "event")
        .map(i => {
          i.data.path = urljoin("/discover/events", `?event_id=${i.data.id}`)

          return i
        })
    },

    nonEventItems() {
      return this.items.filter(i => i.type !== "event")
    },

    hasNoItems() {
      return !this.showEventsBlock && isEmpty(this.nonEventItems)
    },

    creditsText() {
      return this.credits_number === 1 ? "pass" : "passes"
    },

    priceText() {
      return `$${this.product.price_dollars}`
    },

    priceInterval() {
      return this.isRecurring ? `/ ${this.product.interval}` : null
    },

    subscription() {
      return this.userPackSubscription(this.id)
    },

    hasActiveSubscription() {
      // Empty object for subscription, means its active
      return !!this.subscription
    },

    isCanceled() {
      return this.subscription && this.subscription.canceled
    },

    canceledSubtext() {
      const endsAt = this.subscription && this.subscription.current_period_end

      return endsAt ? `Active until ${toDate(endsAt)}` : "Cancel anytime"
    },

    isRecurring() {
      return this.product && this.product.purchase_type === "recurring"
    }
  },

  methods: {
    isPresent,
    toDate,

    ...mapActions("packs", ["fetchSubscriptions", "fetchPackAccess"]),

    handleCtaClick() {
      this.$router.push("#checkout")
    },

    handlePackPurchase() {
      this.showSuccessDialog = true
      this.isLoading = true
      this.fetchSubscriptions().finally(() => (this.isLoading = false))
    },

    goToAccount() {
      this.showSuccessDialog = false
      this.$router.push("/me/account")
    },

    loadCreditsInfo() {
      if (this.isAuthenticated) {
        this.isLoading = true
        this.fetchPackAccess(this.id)
          .then(res => {
            this.remainingCreditsNumber = res.data.remaining_credits_number || 0
          })
          .finally(() => (this.isLoading = false))
      }
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        this.loadCreditsInfo()
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
.pack__passes-subheading {
  margin: $size--1 0 0;
  line-height: $size--6;
  color: $color-ui--grey-70;
  display: flex;
  align-items: center;

  .v-icon {
    font-size: 1.75em;
    margin-right: $size--3;
    color: $color-ui--grey-50;
  }
}

.pack__events .collection {
  padding: 0;
}

.pack__events .collection__heading {
  display: flex;

  .v-link {
    font-size: $font-size--small;
    margin-left: auto;
    color: var(--brand-color-hex);
  }
}
</style>
